import { useState, useMemo, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
  Menu,
  MenuItem,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import DescriptionIcon from "@mui/icons-material/Description";
import ArticleIcon from "@mui/icons-material/Article";
import BusinessIcon from "@mui/icons-material/Business";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BarChartIcon from "@mui/icons-material/BarChart";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import SecurityIcon from "@mui/icons-material/Security";
import RocketIcon from '@mui/icons-material/Rocket'; 
import GroupWorkIcon from '@mui/icons-material/GroupWork'; 
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DrawIcon from "@mui/icons-material/Draw";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import CloudIcon from "@mui/icons-material/Cloud";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GavelIcon from "@mui/icons-material/Gavel";

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const menuItems = useMemo(
    () => [
      { text: "Contact us", link: "/Contactus", icon: <ContactMailIcon /> },
    ],
    []
  );

  const servicesMenu = useMemo(
    () => [
      {
        text: "E-Invoice",
        link: "/services/e-invoice",
        icon: <ReceiptLongIcon />,
      },
      { text: "GST", link: "/services/gst", icon: <AccountBalanceIcon /> },
      { text: "E-Sign", link: "/services/esign", icon: <DrawIcon /> },
      { text: "Chatbot AI", link: "/services/chatbot", icon: <SmartToyIcon /> },
      {
        text: "Report Generation",
        link: "/services/reports",
        icon: <AssessmentIcon />,
      },
      {
        text: "Interactive Charts",
        link: "/services/charts",
        icon: <InsertChartIcon />,
      },
      {
        text: "Cloud Storage",
        link: "/services/cloud-storage",
        icon: <CloudIcon />,
      },
      {
        text: "Task Manager",
        link: "/services/task-manager",
        icon: <TaskAltIcon />,
      },
      {
        text: "Journal Creation",
        link: "/services/journal-creation",
        icon: <MenuBookIcon />,
      },
      {
        text: "Compliances",
        link: "/services/compliances",
        icon: <GavelIcon />,
      },
    ],
    []
  );

  const resourcesMenu = useMemo(
    () => [
      { text: "Vision", link: "/OurVision", icon: <DescriptionIcon /> },
      { text: "Blog", link: "/resources/blog", icon: <ArticleIcon /> },
      {
        text: "Case Studies",
        link: "/resources/case-studies",
        icon: <BusinessIcon />,
      },
    ],
    []
  );

  const featuresMenu = useMemo(
    () => [
      {
        text: "Automation",
        link: "/features/automation",
        icon: <AutoAwesomeIcon />,
      },
      {
        text: "Analytics",
        link: "/features/analytics",
        icon: <BarChartIcon />,
      },
      {
        text: "Integration",
        link: "/features/integration",
        icon: <IntegrationInstructionsIcon />,
      },
      { text: "Security", link: "/features/security", icon: <SecurityIcon /> },
    ],
    []
  );

  const industryMenu = useMemo(
    () => [
      { text: "MSMEs", link: "/industry/msme", icon: <BusinessIcon /> },
      { text: "Startups", link: "/industry/startup", icon: <RocketIcon /> },
      {
        text: "Consulting Firms",
        link: "/industry/consulting",
        icon: <GroupWorkIcon />,
      },
    
    ],
    []
  );

  const handleMenuOpen = useCallback((event, menuName) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuName);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setActiveMenu(null);
  }, []);

  const toggleDrawer = useCallback(
    (open) => (event) => {
      if (
        event.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }
      setDrawerOpen(open);
    },
    []
  );

  const handleLinkClick = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Memoized menu item renderer
  const renderMenuItem = useCallback(
    (item) => (
      <MenuItem
        key={item.text}
        component={Link}
        to={item.link}
        onClick={handleMenuClose}
        sx={{ py: 1, borderRadius: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ color: theme.palette.primary.main, display: "flex" }}>
            {item.icon}
          </Box>
          {item.text}
        </Box>
      </MenuItem>
    ),
    [handleMenuClose, theme.palette.primary.main]
  );

  // Memoized mega menu content
  const menuContent = useMemo(() => {
    const servicesPerColumn = 5;
    const needsTwoColumns = servicesMenu.length > servicesPerColumn;

    switch (activeMenu) {
      case "services":
        return (
          <Grid container spacing={3}>
            {needsTwoColumns ? (
              <>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Our Services
                  </Typography>
                  {servicesMenu.slice(0, servicesPerColumn).map(renderMenuItem)}
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: theme.palette.primary.main,
                    }}
                  >
                    &nbsp;
                  </Typography>
                  {servicesMenu.slice(servicesPerColumn).map(renderMenuItem)}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  Our Services
                </Typography>
                {servicesMenu.map(renderMenuItem)}
              </Grid>
            )}
          </Grid>
        );

      case "resources":
        return (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: theme.palette.primary.main,
                }}
              >
                Resources
              </Typography>
              {resourcesMenu.map(renderMenuItem)}
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: theme.palette.primary.main,
                }}
              >
                Features
              </Typography>
              {featuresMenu.map(renderMenuItem)}
            </Grid>
          </Grid>
        );

      case "industry":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: theme.palette.primary.main,
                }}
              >
                Industries We Serve
              </Typography>
              {industryMenu.map(renderMenuItem)}
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  }, [
    activeMenu,
    servicesMenu,
    resourcesMenu,
    featuresMenu,
    industryMenu,
    renderMenuItem,
    theme.palette.primary.main,
  ]);

  // Memoized menu width calculation
  const menuWidth = useMemo(() => {
    if (activeMenu === "resources") return 450;
    if (activeMenu === "services" && servicesMenu.length > 5) return 500;
    return 280;
  }, [activeMenu, servicesMenu.length]);

  // Logo component
  const Logo = useMemo(
    () => (
      <Link
        to="/"
        style={{
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", padding: "4px" }}>
          <img
            src="/failogo.jpg"
            alt="Logo"
            style={{
              height: isMobile ? "40px" : "50px",
              width: isMobile ? "40px" : "50px",
              marginRight: "10px",
            }}
          />
          <Typography
            variant="h6"
            component="span"
            sx={{
              color: "black",
              fontSize: isMobile ? "1.8rem" : "4rem",
              fontWeight: "300",
               pt: 1,
            }}
          >
            f-
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={{
              color: theme.palette.primary.main,
              fontSize: isMobile ? "1.8rem" : "3rem",
              fontWeight: "300",
              pt: 1,
            }}
          >
            ai
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={{
              color: "black",
              fontSize: isMobile ? "1.8rem" : "3rem",
              fontWeight: "300",
              pt:1,
            }}
          >
            .in
          </Typography>
        </Box>
      </Link>
    ),
    [isMobile, theme.palette.primary.main]
  );

  const drawer = useMemo(
    () => (
      <Box
        sx={{
          width: 280,
          height: "100%",
          backgroundColor: "#F8EEDB",
          display: "flex",
          flexDirection: "column",
        }}
        role="presentation"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src="/failogo.jpg"
              alt="Logo"
              style={{ height: "35px", width: "35px", marginRight: "8px" }}
            />
            <Typography
              variant="h6"
              component="span"
              sx={{ color: "black", fontSize: "1.5rem", fontWeight: "bold" }}
            >
              f-
            </Typography>
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: theme.palette.primary.main,
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              ai
            </Typography>
            <Typography
              variant="h6"
              component="span"
              sx={{ color: "black", fontSize: "1.5rem" }}
            >
              .in
            </Typography>
          </Box>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ flexGrow: 1, paddingTop: "8px" }}>
          <ListItem>
            <ListItemText
              primary="Services"
              primaryTypographyProps={{
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            />
          </ListItem>
          {servicesMenu.map(({ text, link, icon }) => (
            <ListItem
              button
              key={text}
              component={Link}
              to={link}
              onClick={handleLinkClick}
              sx={{ pl: 4 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "inherit",
                }}
              >
                <Box
                  sx={{
                    color: theme.palette.primary.main,
                    display: "flex",
                    fontSize: 20,
                  }}
                >
                  {icon}
                </Box>
                <ListItemText primary={text} />
              </Box>
            </ListItem>
          ))}

          <Divider sx={{ my: 2 }} />

          {menuItems.map(({ text, link, icon }) => (
            <ListItem
              button
              key={text}
              component={Link}
              to={link}
              onClick={handleLinkClick}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "inherit",
                }}
              >
                <Box
                  sx={{
                    color: theme.palette.primary.main,
                    display: "flex",
                    fontSize: 20,
                  }}
                >
                  {icon}
                </Box>
                <ListItemText primary={text} />
              </Box>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ margin: "16px 0" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            padding: "16px",
          }}
        >
          <Button
            component={Link}
            to="/login"
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              borderRadius: "30px",
              background: "linear-gradient(135deg, #EB5A3C 0%, #FF7A5C 100%)",
              color: "#FFFFFF",
              boxShadow: "0 4px 14px rgba(235,90,60,0.4)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #C54428 0%, #EB5A3C 100%)",
                boxShadow: "0 6px 18px rgba(235,90,60,0.5)",
              },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>
    ),
    [
      theme.palette.primary.main,
      toggleDrawer,
      servicesMenu,
      menuItems,
      handleLinkClick,
    ]
  );

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "#F8EEDB",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <Toolbar sx={{ minHeight: { xs: "64px", sm: "70px" },px: { xs: 0, lg: '20%' }}}>
        {isMobile ? (
          <>
            {Logo}
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{ ml: "auto" }}
            >
              <MenuIcon />
            </IconButton>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {Logo}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                color="inherit"
                onClick={(e) => handleMenuOpen(e, "services")}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                Services
              </Button>

              <Button
                color="inherit"
                onClick={(e) => handleMenuOpen(e, "resources")}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                Resources
              </Button>

              <Button
                color="inherit"
                onClick={(e) => handleMenuOpen(e, "industry")}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                Industry
              </Button>

              {menuItems.map(({ text, link, icon }) => (
                <Button
                  color="inherit"
                  key={text}
                  component={Link}
                  to={link}
                  startIcon={icon}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "1rem",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  {text}
                </Button>
              ))}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1.2,
                  borderRadius: "30px",
                  background:
                    "linear-gradient(135deg, #EB5A3C 0%, #FF7A5C 100%)",
                  color: "#FFFFFF",
                  boxShadow: "0 4px 14px rgba(235,90,60,0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #C54428 0%, #EB5A3C 100%)",
                    boxShadow: "0 6px 18px rgba(235,90,60,0.5)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Login
              </Button>
            </Box>
          </Box>
        )}
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: menuWidth,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
        MenuListProps={{
          sx: { py: 2, px: 2 },
        }}
        TransitionProps={{ timeout: 200 }}
      >
        {menuContent}
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
